import React from 'react';
import { Route, IndexRoute, Redirect } from 'react-router';

import withAuthentication from 'hocs/withAuthentication';

import App from './components/App/App';
import Feedback from './components/typeforms/Feedback/Feedback';
import Home from './components/Home/Home';
import InvoiceList from './components/InvoiceList/InvoiceList';
import PauseCancelMembership from './components/typeforms/PauseCancelMembership/PauseCancelMembership';

// Common pages
import SignIn from './pages/Account/SignIn';
import SignUpDetails from './pages/Account/SingupDetails';
import ForgotPassword from './pages/Account/ForgotPassword';
import ResetPassword from './pages/Account/ResetPassword';
import PageNotFound from './components/PageNotFound/PageNotFound';

// Student pages
import OnBoard from './pages/student/OnBoard';
import Activate from './pages/student/Activate';

import Reactivate from './pages/student/Reactivate';
import PaymentFailure from './pages/student/PaymentFailure';
import SuccessPage from './pages/student/Success';
import Offer47 from './pages/student/Offer47';
import OfferLang7 from './pages/student/OfferLang7';
import SpecialOffer from './pages/student/SpecialOffer';
import ReportIssue from './pages/student/ReportIssue';
import IssueSurvey from './pages/student/ReportIssue/routes/IssueSurvey';
import IssueFinished from './pages/student/ReportIssue/routes/IssueFinished';
import ApproveIssue from './pages/student/ReportIssue/routes/ApproveIssue';
import DisputeIssue from './pages/student/ReportIssue/routes/DisputeIssue';

import Reservations from './pages/student/Reservations';
import ScheduleLesson from './pages/student/ScheduleLesson';
import TeacherBooking from './pages/student/TeacherBooking';
import TeacherList from './pages/student/TeacherList';

import Profile from './pages/student/Profile';
import ProfileEdit from './pages/student/Profile/routes/Edit';
import PlansAndBilling from './pages/student/Profile/routes/PlansAndBilling';
import LowerTierPlan from './pages/student/Profile/routes/LowerTierPlan';
import PaymentMethods from './pages/student/Profile/routes/Cards';

// Teacher pages
import TeacherLayout from './components/layouts/TeacherLayout';
import TeacherHome from './pages/teacher/Home';

import TeacherSchedule from './pages/teacher/Schedule';
import TeacherScheduleReservations from './pages/teacher/Schedule/Reservations';
import TeacherScheduleAvailability from './pages/teacher/Schedule/Availability';
import TeacherScheduleListings from './pages/teacher/Schedule/Listings';
import TeacherScheduleListingHome from './pages/teacher/Schedule/Listings/Home';
import TeacherScheduleListingDetails from './pages/teacher/Schedule/Listings/Edit';
import TeacherScheduleListingNew from './pages/teacher/Schedule/Listings/New';

import TeacherSettings from './pages/teacher/Settings';
import TeacherSettingsProfile from './pages/teacher/Settings/Profile';
import TeacherSettingsHelp from './pages/teacher/Settings/Help';
import TeacherCalendarSync from './pages/teacher/Settings/CalendarSync';
import TeacherSettingsPhoto from './pages/teacher/Settings/Photo';
import IssueDetails from './pages/teacher/Schedule/IssueDetails';
import ConfirmLesson from './pages/teacher/ConfirmLesson';

// Admin routes
import Admin from './pages/admin';
import ManageDisputes from './pages/admin/routes/ManageDisputes';
import SearchIssues from './pages/admin/routes/SearchIssues';
import ShowIssue from './pages/admin/routes/ShowIssue';
import ManageTeachers from './pages/admin/routes/ManageTeachers';
import TeacherOnboard from './pages/admin/routes/TeacherOnboard';
import SearchAccounts from './pages/admin/routes/SearchAccounts';
import AppointmentCSV from './pages/admin/routes/AppointmentCSV';
import BadRatings from './pages/admin/routes/BadRatings';

import * as dataInitializers from './utils/routeUtils';

const routes = (
  <Route path="/" component={App}>
    <IndexRoute
      component={withAuthentication(true)(Home)}
      onEnter={dataInitializers.onHomeEnter}
    />

    <Route path="login" component={withAuthentication(true)(SignIn)} />

    <Redirect from="register" to="trial" />

    <Route
      path="signup-details"
      component={withAuthentication(true)(SignUpDetails)}
    />

    <Route path="forgot" component={withAuthentication(true)(ForgotPassword)} />

    <Route path="reset" component={withAuthentication(true)(ResetPassword)} />

    <Route path="start" component={withAuthentication()(OnBoard)} />

    <Route path="trial" component={withAuthentication(true)(Activate)} />

    <Route path="activate" component={withAuthentication(true)(Activate)} />

    <Route path="reactivate" component={withAuthentication()(Reactivate)} />

    <Route path="report-problem">
      <IndexRoute component={withAuthentication()(ReportIssue)} />
      <Route path="thank-you" component={withAuthentication()(IssueFinished)} />
      <Route path=":id" component={withAuthentication()(IssueSurvey)} />
    </Route>

    <Route path="issues/:id/:secret/approve" component={ApproveIssue} />
    <Route path="issues/:id/:secret/dispute" component={DisputeIssue} />

    <Route path="payment-failure" component={PaymentFailure} />

    <Route path="offer-47" component={withAuthentication()(Offer47)} />

    <Route path="offer-lang7" component={withAuthentication()(OfferLang7)} />

    <Route path="success" component={withAuthentication()(SuccessPage)} />

    <Route
      path="schedule-lesson"
      component={withAuthentication()(ScheduleLesson)}
    >
      <IndexRoute component={TeacherList} />
      <Route
        path=":slug"
        component={TeacherBooking}
        onLeave={dataInitializers.onTeacherLeave}
      />
    </Route>

    <Route
      path="reservations"
      component={withAuthentication()(Reservations)}
      onEnter={dataInitializers.onReservationsEnter}
      onLeave={dataInitializers.onReservationsLeave}
    />

    <Route
      path="profile"
      component={withAuthentication()(Profile)}
      onEnter={dataInitializers.onProfileEnter}
    >
      <IndexRoute component={ProfileEdit} />

      <Route path="plans-and-billing" component={PlansAndBilling}>
        <IndexRoute component={LowerTierPlan} />
        <Route path="invoices" component={InvoiceList} />
        <Route path="cards" component={PaymentMethods} />
      </Route>

      <Route path="edit" component={ProfileEdit} />
    </Route>

    <Route path="feedback" component={Feedback} />

    <Route path="pause-cancel" component={PauseCancelMembership} />

    <Route path="confirm-lesson/:lessonId" component={withAuthentication()(ConfirmLesson)} />

    {/* Teacher Routes */}
    <Redirect from="teacher" to="teacher/home" />
    <Redirect from="teacher/schedule" to="teacher/schedule/reservations" />
    <Redirect from="teacher/settings" to="teacher/settings/profile" />
    <Route path="teacher" component={withAuthentication()(TeacherLayout)}>
      <Route path="home" component={TeacherHome} />
      <Route path="schedule" component={TeacherSchedule}>
        <Route path="reservations" component={TeacherScheduleReservations} />
        <Route path="reservations/:id/issue" component={IssueDetails} />
        <Route path="availability" component={TeacherScheduleAvailability} />
        <Route
          path="listings"
          component={TeacherScheduleListings}
          onEnter={dataInitializers.onTeacherListingsEnter}
        >
          <IndexRoute component={TeacherScheduleListingHome} />
          <Route path="new" component={TeacherScheduleListingNew} />
          <Route path=":id" component={TeacherScheduleListingDetails} />
        </Route>
      </Route>
      <Route path="settings" component={TeacherSettings}>
        <Route
          path="profile"
          component={TeacherSettingsProfile}
          onEnter={dataInitializers.onTeacherProfileEnter}
        />
        <Route path="photo" component={TeacherSettingsPhoto} />
        <Route path="calendar-sync/google" component={TeacherCalendarSync} />
        <Route path="calendar-sync/microsoft" component={TeacherCalendarSync} />
        <Route path="calendar-sync" component={TeacherCalendarSync} />
        <Route path="help" component={TeacherSettingsHelp} />
      </Route>
    </Route>

    {/* Admin Routes */}
    <Route path="admin" component={withAuthentication()(Admin)}>
      <IndexRoute component={ManageDisputes} />
      <Route path="disputes" component={ManageDisputes} />
      <Route path="search-issues" component={SearchIssues} />
      <Route path="teachers" component={ManageTeachers} />
      <Route path="issues/:id" component={ShowIssue} />
      <Route path="onboard" component={TeacherOnboard} />
      <Route path="accounts" component={SearchAccounts} />
      <Route path="appointments-csv" component={AppointmentCSV} />
      <Route path="bad-ratings" component={BadRatings} />
    </Route>

    <Route path="*" exact component={PageNotFound} />
  </Route>
);

export default routes;



// WEBPACK FOOTER //
// ./src/routes.js